import { Node, Edge } from 'reactflow';

// Track warned edges to avoid duplicate warnings
const warnedEdges = new Set<string>();

/**
 * Validates edges against current nodes to ensure all connections are valid
 * Removes edges that reference non-existent nodes or handles
 */
export function validateEdges(edges: Edge[], nodes: Node[]): Edge[] {
  const nodeIds = new Set(nodes.map(n => n.id));
  
  return edges.filter(edge => {
    // Check if source and target nodes exist
    if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) {
      // Only warn once per edge to avoid console spam
      if (!warnedEdges.has(edge.id)) {
        if (import.meta.env.DEV) {
          console.warn(`[Edge Validation] Removing invalid edge ${edge.id}: source or target node not found`);
        }
        warnedEdges.add(edge.id);
      }
      return false;
    }
    
    // Find the actual nodes
    const sourceNode = nodes.find(n => n.id === edge.source);
    const targetNode = nodes.find(n => n.id === edge.target);
    
    if (!sourceNode || !targetNode) {
      return false;
    }
    
    // Validate source handle if specified
    if (edge.sourceHandle && edge.sourceHandle !== 'default') {
      // Check if the source node has this handle
      // For now, we'll be lenient and allow it, but log a warning
      // In a more strict implementation, you'd check the node's handle configuration
    }
    
    // Validate target handle if specified
    if (edge.targetHandle && edge.targetHandle !== 'default') {
      // Check if the target node has this handle
      // For now, we'll be lenient and allow it, but log a warning
    }
    
    return true;
  });
}

/**
 * Cleans up edges when a node is deleted
 */
export function removeEdgesForNode(edges: Edge[], nodeId: string): Edge[] {
  return edges.filter(edge => edge.source !== nodeId && edge.target !== nodeId);
}

/**
 * Checks if an edge already exists between two nodes
 */
export function edgeExists(
  edges: Edge[],
  source: string,
  target: string,
  sourceHandle?: string | null,
  targetHandle?: string | null
): boolean {
  return edges.some(
    edge =>
      edge.source === source &&
      edge.target === target &&
      edge.sourceHandle === sourceHandle &&
      edge.targetHandle === targetHandle
  );
}

/**
 * Generates a unique edge ID
 */
export function generateEdgeId(
  source: string,
  target: string,
  sourceHandle?: string | null,
  targetHandle?: string | null
): string {
  const parts = [source, target];
  if (sourceHandle) parts.push(sourceHandle);
  if (targetHandle) parts.push(targetHandle);
  return `reactflow__edge-${parts.join('-')}`;
}
